import SearchIcon from '@mui/icons-material/Search'
import {
  Divider,
  Grid,
  InputAdornment,
  OutlinedInput,
  OutlinedInputProps,
  styled,
  useMediaQuery,
} from '@mui/material'
import { PropertyAdSearchByCriteriaDTO } from 'api/services/PropertyAd/types'
import FilterToggleButton from 'components/shared/FilterToggleButton'
import FilterToggleButtonGroup from 'components/shared/FilterToggleButtonGroup'
import useDebounce from 'hooks/useDebounce'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { theme } from 'theme'
import {
  PropertyAdCategoryTypeEnum,
  PropertyAdStatusEnum,
  PropertyAdTypeEnum,
} from 'types/enums'
import {
  AD_CATEGORY_TYPE_OPTIONS,
  CATEGORY_TYPE_OPTIONS,
  PROPERTY_STATUS_OPTIONS,
} from './constants'

const CustomOutlinedInput = styled(OutlinedInput)<OutlinedInputProps>(() => ({
  paddingRight: 0,
  padding: '8px 11px',
  '& .MuiOutlinedInput-input': {
    width: '158px',
    color: 'black',
    padding: 0,
    '&::placeholder': {
      opacity: 1,
      fontStyle: 'italic',
    },
  },
  '& .MuiInputAdornment-root': {
    marginLeft: '0px',
    color: 'black',
  },
  '& .MuiOutlinedInput-root': {
    fontWeight: 400,
    width: '203px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
    border: '1px solid #EBEBEB',
    boxShadow: '3px 3px 8px 0px #0909094D',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '8px 4px',
    fontSize: '14px',
    '& .MuiOutlinedInput-input': {
      width: '134px',
      color: 'black',
      lineHeight: '16px',
      padding: 0,
    },
    '& .MuiOutlinedInput-root': {
      fontWeight: 400,
      width: '167px',
    },
  },
}))

export interface Filter {
  type: string
  value: string | null
}

export interface FiltersProps {
  onFilterChange: (newFilters: Partial<PropertyAdSearchByCriteriaDTO>) => void
}

export default function Filters(filtersProps: FiltersProps) {
  const { t } = useTranslation()

  const [filters] = useState<Partial<PropertyAdSearchByCriteriaDTO>>({})
  const [adCategoryType, setAdCategoryType] =
    useState<PropertyAdTypeEnum | null>(null)
  const [categoryType, setCategoryType] =
    useState<PropertyAdCategoryTypeEnum | null>(null)
  const [propertyStatus, setPropertyStatus] =
    useState<PropertyAdStatusEnum | null>(null)
  const [propertyArea, setPropertyArea] = useState<string | null>(null)
  const [propertyCode, setPropertyCode] = useState<string | null>(null)

  const { onFilterChange } = filtersProps

  function handleAdType(
    event: React.MouseEvent<HTMLElement>,
    newValue: PropertyAdTypeEnum | null
  ) {
    setAdCategoryType(newValue)
    filters['propertyAdType'] = newValue
    onFilterChange(filters)
  }

  function handleCategoryType(
    event: React.MouseEvent<HTMLElement>,
    newValue: PropertyAdCategoryTypeEnum | null
  ) {
    setCategoryType(newValue)
    filters['propertyAdCategoryType'] = newValue
    onFilterChange(filters)
  }

  function handlePropertyStatus(
    event: React.MouseEvent<HTMLElement>,
    newValue: PropertyAdStatusEnum | null
  ) {
    setPropertyStatus(newValue)
    filters['propertyAdStatus'] = newValue
    onFilterChange(filters)
  }

  function handlePropertyArea(e) {
    filters['area'] = e.target.value.length ? e.target.value : null
    onFilterChange(filters)
  }

  const debouncedPropertyArea = useDebounce(handlePropertyArea, 500)

  function handlePropertyCode(e) {
    filters['externalId'] = e.target.value.length ? e.target.value : null
    onFilterChange(filters)
  }

  const debouncedPropertyCode = useDebounce(handlePropertyCode, 500)
  const matchesSM = useMediaQuery(theme.breakpoints.down('md'))
  const matchesXS = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <>
      {matchesXS ? (
        <Grid
          container
          direction="row"
          display="flex"
          alignItems="end"
          justifyContent="end"
          sx={{ gap: '10px' }}
        >
          <CustomOutlinedInput
            type={'text'}
            value={propertyArea ?? ''}
            onChange={(e) => {
              setPropertyArea(e.target.value)
              debouncedPropertyArea(e)
            }}
            size="small"
            placeholder={t('tables.propertyAds.filters.searchArea')}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
          />
          <CustomOutlinedInput
            type={'text'}
            value={propertyCode ?? ''}
            onChange={(e) => {
              setPropertyCode(e.target.value)
              debouncedPropertyCode(e)
            }}
            size="small"
            placeholder={t('tables.propertyAds.filters.searchCode')}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
          />
        </Grid>
      ) : null}
      {matchesXS ? (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          sx={{ mt: { xs: '12px', md: '20px' } }}
        >
          <FilterToggleButtonGroup
            value={propertyStatus}
            exclusive
            onChange={handlePropertyStatus}
          >
            {PROPERTY_STATUS_OPTIONS.map((propertyStatus) => (
              <FilterToggleButton
                size="small"
                value={propertyStatus.value}
                key={propertyStatus.value}
                sx={{
                  boxShadow: 4,
                  color: 'common.black',
                  fontWeight: { xs: 500, md: 700 },
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {t(propertyStatus.label)}
              </FilterToggleButton>
            ))}
          </FilterToggleButtonGroup>
        </Grid>
      ) : null}
      <Grid
        container
        direction={matchesSM ? 'column' : 'row'}
        display="flex"
        alignItems={matchesSM ? 'end' : 'start'}
        sx={{ gap: '16px', mt: { xs: '12px' } }}
      >
        <FilterToggleButtonGroup
          value={adCategoryType}
          exclusive
          onChange={handleAdType}
        >
          {AD_CATEGORY_TYPE_OPTIONS.map((adType) => (
            <FilterToggleButton
              size="small"
              key={adType.value}
              value={adType.value}
              sx={{
                boxShadow: 4,
                color: 'common.black',
                fontWeight: { xs: 500, md: 700 },
                fontSize: { xs: '14px', md: '16px' },
              }}
            >
              {t(adType.label)}
            </FilterToggleButton>
          ))}
        </FilterToggleButtonGroup>
        {matchesSM ? null : (
          <Divider
            orientation="vertical"
            variant="fullWidth"
            flexItem
            sx={{ mx: 2, bgcolor: 'black' }}
          />
        )}
        <FilterToggleButtonGroup
          value={categoryType}
          exclusive
          onChange={handleCategoryType}
        >
          {CATEGORY_TYPE_OPTIONS.map((catType) => (
            <FilterToggleButton
              size="small"
              value={catType.value}
              key={catType.value}
              sx={{
                boxShadow: 4,
                color: 'common.black',
                fontWeight: { xs: 500, md: 700 },
                fontSize: { xs: '14px', md: '16px' },
              }}
            >
              {t(catType.label)}
            </FilterToggleButton>
          ))}
        </FilterToggleButtonGroup>
      </Grid>
      {matchesXS ? null : (
        <Grid
          container
          direction="row"
          justifyContent="flex-end"
          sx={{ mt: { xs: '12px', md: '20px' } }}
        >
          <FilterToggleButtonGroup
            value={propertyStatus}
            exclusive
            onChange={handlePropertyStatus}
          >
            {PROPERTY_STATUS_OPTIONS.map((propertyStatus) => (
              <FilterToggleButton
                size="small"
                value={propertyStatus.value}
                key={propertyStatus.value}
                sx={{
                  boxShadow: 4,
                  color: 'common.black',
                  fontWeight: { xs: 500, md: 700 },
                  fontSize: { xs: '14px', md: '16px' },
                }}
              >
                {t(propertyStatus.label)}
              </FilterToggleButton>
            ))}
          </FilterToggleButtonGroup>
        </Grid>
      )}
      {matchesXS ? null : (
        <Grid
          container
          direction="row"
          display="flex"
          alignItems="end"
          justifyContent="end"
          sx={{ mt: '20px', gap: '20px' }}
        >
          <CustomOutlinedInput
            type={'text'}
            value={propertyArea ?? ''}
            onChange={(e) => {
              setPropertyArea(e.target.value)
              debouncedPropertyArea(e)
            }}
            placeholder={t('tables.propertyAds.filters.searchArea')}
            size="small"
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
          <CustomOutlinedInput
            type={'text'}
            value={propertyCode ?? ''}
            onChange={(e) => {
              setPropertyCode(e.target.value)
              debouncedPropertyCode(e)
            }}
            placeholder={t('tables.propertyAds.filters.searchCode')}
            size="small"
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
        </Grid>
      )}
    </>
  )
}
